// src/hooks/useAudio.ts
import { useState, useRef, useCallback, useEffect } from 'react';
import { useUI } from '../contexts/UIContext';

export const useAudio = () => {
  const { state: uiState, dispatch } = useUI();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const play = useCallback(async (audioUrl: string) => {
    try {
      // Stop current audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      dispatch({ type: 'SET_AUDIO_PLAYING', payload: true });
      dispatch({ type: 'SET_CURRENT_AUDIO_URL', payload: audioUrl });

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        dispatch({ type: 'SET_AUDIO_PLAYING', payload: false });
        dispatch({ type: 'SET_CURRENT_AUDIO_URL', payload: null });
        audioRef.current = null;
      };

      audio.onerror = () => {
        setError('Failed to play audio');
        dispatch({ type: 'SET_AUDIO_PLAYING', payload: false });
        dispatch({ type: 'SET_CURRENT_AUDIO_URL', payload: null });
        audioRef.current = null;
      };

      await audio.play();
      setError(null);
    } catch (err) {
      setError((err as Error).message || 'Failed to play audio');
      dispatch({ type: 'SET_AUDIO_PLAYING', payload: false });
      dispatch({ type: 'SET_CURRENT_AUDIO_URL', payload: null });
    }
  }, [dispatch]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    dispatch({ type: 'SET_AUDIO_PLAYING', payload: false });
    dispatch({ type: 'SET_CURRENT_AUDIO_URL', payload: null });
  }, [dispatch]);

  const toggle = useCallback((audioUrl: string) => {
    if (uiState.isAudioPlaying && uiState.currentAudioUrl === audioUrl) {
      stop();
    } else {
      play(audioUrl);
    }
  }, [uiState.isAudioPlaying, uiState.currentAudioUrl, play, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  return {
    isPlaying: uiState.isAudioPlaying,
    currentAudioUrl: uiState.currentAudioUrl,
    error,
    play,
    stop,
    toggle,
  };
};